import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import config from '../config';
import { attemptOpenApp, getMobileStoreUrl, MOBILE_STORES } from '../utils/mobileAppRedirect';
import '../styles/deeplink.css';

/**
 * Deep link landing page for user profile sharing
 * Detects if app is installed and redirects accordingly
 * URL pattern: /user/:userUuid or /u/:userUuid
 */
const UserDeepLink = () => {
    const { userUuid } = useParams();
    const navigate = useNavigate();
    const [detecting, setDetecting] = useState(true);
    const [userData, setUserData] = useState(null);

    useEffect(() => {
        if (!userUuid) {
            navigate('/dashboard');
            return;
        }

        // Fetch user data for metadata
        const fetchUserData = async () => {
            try {
                const response = await fetch(`${config.backendUrl}/users/public/${userUuid}/`);
                if (response.ok) {
                    const data = await response.json();
                    setUserData(data);
                }
            } catch (error) {
                console.error('Error fetching user data:', error);
            }
        };

        fetchUserData();

        // Attempt to detect and redirect to app
        let isMounted = true;
        const attemptAppRedirect = async () => {
            const deepLink = `wennze://user/${userUuid}`;
            const webFallback = `/user/corner/${userUuid}`;
            const storeFallback = getMobileStoreUrl();

            const opened = await attemptOpenApp({
                deepLink,
                webFallbackPath: webFallback,
            });

            if (!isMounted || opened) return;

            setDetecting(false);
            setTimeout(() => {
                if (storeFallback) {
                    window.location.replace(storeFallback);
                    return;
                }
                navigate(webFallback);
            }, 900);
        };

        attemptAppRedirect();

        return () => {
            isMounted = false;
        };
    }, [userUuid, navigate]);

    return (
        <div className="deeplink-container">
            <div className="deeplink-content">
                {detecting ? (
                    <>
                        <div className="deeplink-spinner"></div>
                        <h2>Ouverture de Wennze...</h2>
                        <p>Nous ouvrons ce profil dans l'application. Si elle n'est pas installée, vous serez redirigé vers le store adapté à votre téléphone.</p>
                    </>
                ) : (
                    <>
                        <div className="deeplink-icon">W</div>
                        <h2>Application non détectée</h2>
                        <p>{getMobileStoreUrl() ? 'Redirection vers la page de téléchargement...' : 'Vous pouvez consulter ce profil sur le web.'}</p>
                        {userData && (
                            <div className="user-preview">
                                <h3>{userData.first_name} {userData.last_name}</h3>
                                <p>@{userData.username}</p>
                                <p>Note {userData.average_rating || 0}/5 • {userData.follower_count} clients</p>
                            </div>
                        )}
                    </>
                )}

                <div className="deeplink-links">
                    <a
                        href={`/user/corner/${userUuid}`}
                        className="web-link"
                        onClick={(e) => {
                            e.preventDefault();
                            navigate(`/user/corner/${userUuid}`);
                        }}
                    >
                        Ouvrir dans le navigateur
                    </a>
                    <div className="app-download-links">
                        <p>Télécharger Wennze :</p>
                        <div className="store-buttons">
                            <a
                                href={MOBILE_STORES.ios}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="store-button ios-button"
                            >
                                App Store
                            </a>
                            <a
                                href={MOBILE_STORES.android}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="store-button android-button"
                            >
                                Google Play
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserDeepLink;
